import axios from 'axios';

const DEFAULT_MAX_ATTEMPTS = 3;
const BASE_BACKOFF_MS = 1000;

/**
 * Whether another attempt could plausibly succeed.
 *
 * No response at all means the connection died partway through the body, which
 * on a phone on a building site is the ordinary case and exactly what retrying
 * is for. A 4xx is S3 refusing the request itself — an expired signature, a
 * content-type mismatch — and refusing it again costs the user another upload
 * of the whole file for nothing.
 */
const isRetryable = (error) => {
  if (axios.isCancel(error)) return false;
  if (!error || !error.response) return true;
  const status = error.response.status;
  return !(status >= 400 && status < 500);
};

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * PUTs a file to S3, retrying when the connection drops.
 *
 * Takes `getSignedUrl` rather than a URL because upload URLs are signed for 120
 * seconds: by the time a failed attempt has backed off, the URL it used is
 * likely past that, and reusing it would turn a retryable network blip into a
 * guaranteed 403. Each attempt therefore signs afresh.
 *
 * Throws `Error('Upload canceled')` when the caller's token is cancelled, which
 * is the signal callers already distinguish from a failure.
 */
export const uploadFileBySignedUrl = async ({
  file,
  getSignedUrl,
  cancelToken,
  onUploadProgress,
  maxAttempts = DEFAULT_MAX_ATTEMPTS,
}) => {
  let lastError;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      const signedUrl = await getSignedUrl();

      await axios.put(signedUrl, file, {
        cancelToken,
        headers: { 'Content-Type': file.type },
        onUploadProgress,
      });

      return;
    } catch (error) {
      lastError = error;

      if (axios.isCancel(error)) throw new Error('Upload canceled');
      if (attempt === maxAttempts || !isRetryable(error)) throw error;

      console.warn(
        `Upload of ${file.name} failed (attempt ${attempt}/${maxAttempts}), retrying`,
        error
      );

      await wait(BASE_BACKOFF_MS * Math.pow(2, attempt - 1));

      // Backing off is not free: cancelling during the wait has to stop the
      // next attempt rather than start one the user has already dismissed.
      if (cancelToken && cancelToken.reason) {
        throw new Error('Upload canceled');
      }
    }
  }

  throw lastError;
};

export default uploadFileBySignedUrl;
